import React, { useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import { Box, Typography } from "@mui/material";
import {
  AUTH_TYPES,
  COLOR_FACEBOOK,
  PRIMARY,
  TYPES,
  FORM_GAP,
  MODES,
  ERROR
} from "../../constants";
import Spinner from "../Spinner";
import SocialButtonContent from "./SocialButtonContent";
import { useSignInSignUp } from "../../hooks/useSignInSignUp";
import { validate, isUserHasAccount } from "./ConnexionForm.services";
import { renderErrorMessage } from "../../utils";
import {
  analyticsEvents,
  PAGES,
  PROVENANCE,
} from "../../utils/firebaseAnalytics";

import { InputField, Button, Divider } from "@dishopsaas/design-system";

import styles from "./ConnexionForm.styles";
import {
  ConnexionFormProps,
  ConnexionFormValues,
} from "../../1_types/signinTypes";

const ConnexionForm: React.FC<ConnexionFormProps> = ({
  facebookOrGoogleSignIn,
  onSubmit,
  errorMessage,
}) => {
  const {
    loadingConnexion,
    facebook,
    google,
    handleShowForgetPassword,
    authMode,
    email
  } = useSignInSignUp();

  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [emailExistenceError, setEmailExistenceError] = useState("");

  useEffect(() => {
    analyticsEvents.logPageViewEvent(PAGES.LOGIN, PROVENANCE.MENU_PAGE);
  }, []);

  useEffect(() => {
    if (email) {
      analyticsEvents.logPageViewEvent(
        PAGES.FINAL_LOGIN,
        PROVENANCE.LOGIN_PAGE
      );
    }
  }, [email]);

  const validatePassword = (value: string) => value.length >= 6;
  const renderInput = ({ input, placeholder, meta }: any) => {
    const type = input.name;
    const isEmail = type === TYPES.EMAIL;
    const isPassword = type === TYPES.PASSWORD;
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isEmail && emailExistenceError) {
        setEmailExistenceError("");
      }
      input.onChange(e);
    };
    const labelAdornment =
      authMode === MODES.LOGIN && isPassword && !loadingConnexion ? (
        <Typography
          variant="body1"
          sx={styles.forgetPasswordSx}
          onClick={() => handleShowForgetPassword(true)}
        >
          Mot de passe oublié ?
        </Typography>
      ) : undefined;

    const validation =
      authMode === MODES.LOGIN && isPassword
        ? {
          type: "custom" as const,
          customValidation: validatePassword,
          errorMessage:
            "Votre mot de passe doit contenir au moins 6 caractéres",
        }
        : undefined;
    const handleChange = isEmail ? handleEmailChange : input.onChange;
    return (
      <InputField
        label={isEmail ? "Adresse e-mail" : "Mot de passe"}
        placeholder={placeholder}
        value={isEmail && email ? email : input.value}
        disabled={isEmail && email}
        onChange={handleChange}
        error={(meta.touched && meta.error) || (isEmail && emailExistenceError)}
        helperText={
          (meta.touched && meta.error) || (isEmail && emailExistenceError)
        }
        type={type}
        labelAdornment={labelAdornment}
        validation={validation}
      />
    );
  };

  const onSubmitEmail = (email, formValues) => {
    onSubmit(email || formValues.email, formValues.password);
  };

  const handleOnSubmit = async (formValues) => {
    if (!email) {
      setIsButtonLoading(true);
      await isUserHasAccount(formValues.email);
      setIsButtonLoading(false);
    } else {
      onSubmitEmail(email, formValues);
    }
  };

  const hasSocialButtons =
    (facebook?.auth || google?.auth) && !loadingConnexion;

  return (
    <Form<ConnexionFormValues>
      validate={(formValues) => validate(email, formValues, !!email)}
      onSubmit={handleOnSubmit}
      render={({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "40px",
                gap: FORM_GAP,
              }}
            >
              {errorMessage && (
                <Typography typography='body1' sx={{ mb: 2, color: "red" }}>
                  {renderErrorMessage(errorMessage)}
                </Typography>
              )}

              <Field
                name="email"
                type="email"
                placeholder="Email"
                render={renderInput}
              />

              {email && (
                <Field
                  name="password"
                  type="password"
                  placeholder="Mot de passe"
                  render={renderInput}
                />
              )}

              {loadingConnexion && (
                <Box mt={3} textAlign="center">
                  <Spinner />
                </Box>
              )}

              {hasSocialButtons && (
                <Divider
                  flexItem
                  sx={{
                    width: "100%",
                    "&::before, &::after": {
                      borderTopWidth: 1,
                    },
                  }}
                >
                  <Typography variant="body1" sx={styles.dividerText}>
                    OU
                  </Typography>
                </Divider>
              )}

              {hasSocialButtons && (
                <Box
                  sx={{
                    display: "contents",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  {facebook?.auth && (
                    <Button
                      borderRadius={8}
                      label=""
                      withoutHover
                      onClick={() =>
                        facebookOrGoogleSignIn(AUTH_TYPES.FACEBOOK)
                      }
                      sx={{
                        backgroundColor: COLOR_FACEBOOK,
                        border: "none",
                      }}
                      size="large"
                      width="100%"
                    >
                      <SocialButtonContent
                        image="facebook"
                        label="CONNEXION AVEC FACEBOOK"
                      />
                    </Button>
                  )}

                  {google?.auth && (
                    <Button
                      colorType="tertiary"
                      borderRadius={8}
                      label=""
                      withoutHover
                      onClick={() => facebookOrGoogleSignIn(AUTH_TYPES.GOOGLE)}
                      size="large"
                      width="100%"
                    >
                      <SocialButtonContent
                        image="google"
                        label="CONNEXION AVEC GOOGLE"
                      />
                    </Button>
                  )}
                </Box>
              )}
            </Box>
            {!loadingConnexion && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: FORM_GAP,
                }}
              >
                <Button
                  type={PRIMARY}
                  label={email ? "CONNEXION" : "CONTINUER"}
                  disabled={!values?.email && !email}
                  loading={isButtonLoading}
                  sx={{
                    width: "100%",
                    flex: "unset",
                  }}
                  size="large"
                />
              </Box>
            )}
          </Box>
        </form>
      )}
    />
  );
};

export default ConnexionForm;
